import { z } from 'zod'

const summarySchema = z.object({
  id: z.number().int(),
  name: z.string(),
  number: z.string().nullish(),
  seriesId: z.number().int().nullish(),
  seriesName: z.string().nullish(),
  durationMinutes: z.number().int(),
  platform: z.string(),
  memoryMb: z.number().int(),
  sharedEnvironmentRole: z.string(),
  isExam: z.boolean(),
  isEnabled: z.boolean(),
  organizationId: z.string(),
  organizationName: z.string(),
})

/** A lab profile as the lab picker and Find Labs list it. Its id is Lab on Demand's. */
export type LabProfileSummary = z.infer<typeof summarySchema>

export function parseLabProfileSummary(item: unknown): LabProfileSummary {
  return summarySchema.parse(item)
}

const instructionSetSchema = z.object({
  id: z.string(),
  language: z.string(),
  name: z.string().nullish(),
  durationMinutes: z.number().int(),
})

const labProfileSchema = summarySchema.extend({
  description: z.string().nullish(),
  seriesDescription: z.string().nullish(),
  objective: z.string().nullish(),
  scenario: z.string().nullish(),
  // Left out unless the caller holds `lab-profiles` `pricing`.
  price: z.number().nullish(),
  participantPrice: z.number().nullish(),
  sharedEnvironmentPartnerId: z.number().int().nullish(),
  instructionSets: z.array(instructionSetSchema),
  changedInLabOnDemandOn: z.string(),
})

/** A lab profile's facts, as its details page shows them. */
export type LabProfile = z.infer<typeof labProfileSchema>

export function parseLabProfile(body: unknown): LabProfile {
  return labProfileSchema.parse(body)
}

const labSeriesSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  description: z.string().nullish(),
  labProfileCount: z.number().int(),
})

/** A Lab on Demand lab series, counting only the lab profiles the caller may see. */
export type LabSeries = z.infer<typeof labSeriesSchema>

export function parseLabSeries(item: unknown): LabSeries {
  return labSeriesSchema.parse(item)
}

/** The API's lab profile endpoints. */
export const labProfilesApi = '/catalog/v1/lab-profiles'

export function labProfileApi(id: string): string {
  return `${labProfilesApi}/${encodeURIComponent(id)}`
}

/** The API's lab series endpoints. */
export const labSeriesApi = '/catalog/v1/lab-series'

export function oneLabSeriesApi(id: string): string {
  return `${labSeriesApi}/${encodeURIComponent(id)}`
}

/** What the lab picker and the lab pages need. */
export const readLabProfilesPermission = 'catalog.lab-profiles.read'
